import { layoutRect, layoutCoordinate, viewportHeight as layoutViewportHeight, viewportScrollY } from "../utils/displayDensity";
/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";

/** =========================
 *  Sources (public .csv.gz)
 *  ========================= */
const ROBLOX_DB_URL =
  "https://roblox-scrape-testing.s3.us-east-1.amazonaws.com/roblox_database.csv.gz";
const ROBLOX_RECENT_URL =
  "https://roblox-scrape-testing.s3.us-east-1.amazonaws.com/roblox_recent_data.csv.gz";

/** =========================
 *  Helpers
 *  ========================= */

/** Fetch a .csv.gz and return CSV text (uses native DecompressionStream when available). */
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  if ("DecompressionStream" in window && res.body) {
    const ds = new DecompressionStream("gzip");
    const decompressed = res.body.pipeThrough(ds);
    return await new Response(decompressed).text();
  }
  return await res.text();
}

/** Robust CSV parser that respects quotes and escaped quotes (""). */
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let i = 0;
  let inQuotes = false;

  const pushField = () => {
    if (inQuotes && field.indexOf('""') !== -1)
      field = field.replace(/""/g, '"');
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        const next = text[i + 1];
        if (next === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      } else {
        field += c;
        i++;
        continue;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
        i++;
        continue;
      }
      if (c === ",") {
        pushField();
        i++;
        continue;
      }
      if (c === "\r") {
        i++;
        continue;
      }
      if (c === "\n") {
        pushField();
        pushRow();
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
  }
  if (field.length > 0 || row.length > 0) {
    pushField();
    pushRow();
  }

  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim());
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const cols = rows[r];
    if (!cols || cols.every((v) => v === "")) continue;
    const obj = {};
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = (cols[j] ?? "").trim();
    }
    out.push(obj);
  }
  return out;
}

const toNum = (v, d = 0) => {
  if (v == null || v === "") return d;
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normTitle = (t) => (t || "").toLowerCase().replace(/\s+/g, " ").trim();

/** Normalize thumbs if they contain “-{width}x{height}” tokens (safe no-op for rbxcdn) */
function normalizeThumb(url) {
  if (!url) return "";
  const ig = url.match(/(.+?)_IGDB-\{width\}x\{height\}(\.\w{3,4})$/i);
  if (ig) return `${ig[1]}_IGDB${ig[2]}`;
  const gen = url.match(/(.+?)-\{width\}x\{height\}(\.\w{3,4})$/i);
  if (gen) return `${gen[1]}${gen[2]}`;
  return url;
}

const take = (arr, n = 25) => arr.slice(0, n);

/** =========================
 *  Card UI (tilt/shine/remove)
 *  ========================= */
const CARD_WIDTH = 220;

function RobloxCard({ game, index, onClick, onRemove }) {
  const handleEnter = (e) => {
    const card = e.currentTarget.querySelector("[data-card]");
    if (!card) return;
    card.style.transition = "transform 220ms cubic-bezier(.2,.8,.2,1)";
    card.style.willChange = "transform";
    card.style.transform = "perspective(900px) translateZ(0) scale(1.02)";
  };
  const handleMove = (e) => {
    const card = e.currentTarget.querySelector("[data-card]");
    if (!card) return;
    const r = layoutRect(e.currentTarget);
    const x = (layoutCoordinate(e.clientX) - r.left) / r.width;
    const y = (layoutCoordinate(e.clientY) - r.top) / r.height;
    const px = x - 0.5;
    const py = y - 0.5;
    const maxTilt = 8,
      scale = 1.04;
    const rx = -(py * maxTilt);
    const ry = px * maxTilt;
    card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;
    card.style.setProperty("--mx", `${x * 100}%`);
    card.style.setProperty("--my", `${y * 100}%`);
    card.classList.add("mg-tilt-glare");
  };
  const handleLeave = (e) => {
    const card = e.currentTarget.querySelector("[data-card]");
    if (!card) return;
    card.style.transition = "transform 300ms ease";
    card.style.transform = "perspective(900px) rotateX(0) rotateY(0) scale(1)";
    card.classList.remove("mg-tilt-glare");
  };

  const growth = game.ccuDoD ?? 0;
  const failed = game.__imgFailed;

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        position: "relative",
        width: CARD_WIDTH,
        borderRadius: 10,
        overflow: "hidden",
        background: "#111",
        boxShadow: "0 8px 20px rgba(0,0,0,.5)",
        cursor: "pointer",
      }}
      onClick={() => onClick?.(game)}
    >
      <style>{`
        [data-card]:hover {
          box-shadow: 0 18px 30px rgba(0,0,0,.45), 0 0 20px rgba(63,169,245,.25);
        }
        [data-card].mg-tilt-glare::before {
          content: "";
          position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(220px 160px at var(--mx,50%) var(--my,50%),
                      rgba(255,255,255,.14), rgba(255,255,255,0) 55%);
          mix-blend-mode: screen;
        }
      `}</style>

      <div data-card style={{ position: "relative" }}>
        {game.imageUrl ? (
          <img
            src={game.imageUrl}
            alt={game.name}
            onLoad={() => game.__onImgLoad?.(game.key, game.imageUrl)}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              game.__onImgError?.(game.key, game.imageUrl);
            }}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: 120,
              background: "linear-gradient(135deg,#1a1a1a,#222 50%,#1a1a1a)",
            }}
          />
        )}

        <div
          style={{
            position: "absolute",
            top: 6,
            left: 6,
            background: "rgba(0,0,0,.6)",
            color: "#fff",
            padding: "2px 6px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 800,
            pointerEvents: "none",
          }}
        >
          #{index + 1}
        </div>

        {failed && (
          <div
            style={{
              position: "absolute",
              top: 6,
              right: 26,
              background: "rgba(255,0,0,0.75)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 800,
              padding: "0 5px",
              borderRadius: 10,
            }}
          >
            img err
          </div>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.(game.key);
          }}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontSize: 12,
            padding: "0 6px",
            border: "none",
            borderRadius: 14,
            height: 20,
            lineHeight: "20px",
            fontWeight: 700,
            cursor: "pointer",
            zIndex: 11,
          }}
          aria-label="Remove game"
        >
          ×
        </button>

        <div
          style={{
            padding: 10,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
          }}
        >
          <div
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 14,
              lineHeight: 1.1,
              marginBottom: 8,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={game.name}
          >
            {game.name}
          </div>
          <div style={{ color: "#cfd8e3", fontSize: 13, marginBottom: 4 }}>
            🟢 {game.ccu.toLocaleString()} CCU{" "}
            {growth > 0 ? (
              <span style={{ color: "#6cf" }}>
                (+{growth.toLocaleString()})
              </span>
            ) : growth < 0 ? (
              <span style={{ color: "#f55" }}>
                (–{Math.abs(growth).toLocaleString()})
              </span>
            ) : (
              <span style={{ color: "#777" }}>(0)</span>
            )}
          </div>
          <div style={{ color: "#cfd8e3", fontSize: 13 }}>
            👁️ {game.visits.toLocaleString()} visits
          </div>
        </div>
      </div>
    </div>
  );
}

function RobloxCardGrid({ games, loading, onCardClick, onRemove }) {
  const [showCount, setShowCount] = useState(120);

  useEffect(() => setShowCount(120), [games]);

  useEffect(() => {
    const onScroll = () => {
      if (
        layoutViewportHeight() + viewportScrollY() + 600 >=
        document.documentElement.offsetHeight
      ) {
        setShowCount((n) => Math.min(n + 80, games.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [games.length]);

  const slice = loading ? [] : games.slice(0, showCount);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr))`,
        gap: 16,
      }}
    >
      {loading
        ? Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              style={{
                width: CARD_WIDTH,
                height: 215,
                background: "#111",
                borderRadius: 10,
              }}
            />
          ))
        : slice.map((g, i) => (
            <RobloxCard
              key={g.key}
              game={g}
              index={i}
              onClick={onCardClick}
              onRemove={onRemove}
            />
          ))}
    </div>
  );
}

/** =========================
 *  Popout (simple details)
 *  ========================= */
function GameModal({ game, onClose }) {
  if (!game) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.65)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(960px,calc(var(--mg-vw) * 95))",
          maxHeight: "calc(var(--mg-vh) * 85)",
          overflow: "auto",
          background: "#0f0f0f",
          border: "1px solid #333",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.6)",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #222",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ color: "#3fa9f5", fontSize: 18, fontWeight: 800 }}>
            {game.name}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#222",
              border: "1px solid #555",
              color: "#fff",
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Close
          </button>
        </div>

        <div
          style={{
            padding: 16,
            display: "grid",
            gridTemplateColumns: "320px 1fr",
            gap: 16,
          }}
        >
          <div>
            {game.imageUrl && (
              <img
                src={game.imageUrl}
                alt={game.name}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: 8,
                  border: "1px solid #222",
                }}
              />
            )}
          </div>
          <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
            <div style={{ color: "#cfd8e3", fontSize: 14 }}>
              <strong style={{ color: "#fff" }}>Title:</strong> {game.name}
            </div>
            <div style={{ color: "#cfd8e3", fontSize: 14 }}>
              <strong style={{ color: "#fff" }}>CCU:</strong>{" "}
              {game.ccu.toLocaleString()}{" "}
              {game.ccuDoD > 0 ? (
                <span style={{ color: "#6cf" }}>
                  (+{game.ccuDoD.toLocaleString()})
                </span>
              ) : game.ccuDoD < 0 ? (
                <span style={{ color: "#f55" }}>
                  (–{Math.abs(game.ccuDoD).toLocaleString()})
                </span>
              ) : (
                <span style={{ color: "#777" }}>(0)</span>
              )}
            </div>
            <div style={{ color: "#cfd8e3", fontSize: 14 }}>
              <strong style={{ color: "#fff" }}>Cumulative Visits:</strong>{" "}
              {game.visits.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** =========================
 *  Page (TITLE join)
 *  ========================= */
export default function Roblox() {
  const [search, setSearch] = useState("");
  const [hidden, setHidden] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const [games, setGames] = useState([]); // {key, name, imageUrl, ccu, visits, ccuDoD}
  const [selected, setSelected] = useState(null);

  // ===== DEBUG STATE =====
  const [dbgOpen, setDbgOpen] = useState(false);
  const [dbg, setDbg] = useState({
    dbHeaders: [],
    recentHeaders: [],
    dbRows: 0,
    recentRows: 0,
    metaMapSize: 0,
    uniqueRecentTitles: 0,
    joined: 0,
    joinedWithImage: 0,
    sampleJoined: [],
  });
  const [imgErrors, setImgErrors] = useState(new Map()); // key (normTitle) -> {url, ts}
  const [imgLoads, setImgLoads] = useState(new Map()); // key (normTitle) -> url
  const onImgError = (key, url) =>
    setImgErrors((m) => new Map(m).set(key, { url, ts: Date.now() }));
  const onImgLoad = (key, url) => setImgLoads((m) => new Map(m).set(key, url));

  /** Load DB + Recent, join on **title**; compute latest and DoD */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        // 1) Load DB (images)
        const dbText = await fetchGzipCsvText(ROBLOX_DB_URL);
        const dbRows = parseCSV(dbText);
        const dbHeaders =
          dbText
            .split(/\r?\n/)[0]
            ?.split(",")
            .map((h) => h.trim()) || [];

        const metaMapTitle = new Map();
        for (const r of dbRows) {
          const title = (r.title || r.name || "").trim();
          const key = normTitle(title);
          if (!key) continue;

          const rawImg =
            r.image || r.thumbnail_url || r.thumbnail || r.image_url || "";
          const normalizedImg = normalizeThumb(String(rawImg || "").trim());
          const isGoodImg =
            normalizedImg &&
            normalizedImg.toUpperCase() !== "[NO IMAGE]" &&
            !/^\[NO\s*IMAGE\]$/i.test(normalizedImg);

          if (!metaMapTitle.has(key)) {
            metaMapTitle.set(key, {
              key,
              name: title,
              imageUrl: isGoodImg ? normalizedImg : "",
            });
          } else {
            const prev = metaMapTitle.get(key);
            const haveGoodAlready =
              prev.imageUrl &&
              prev.imageUrl.toUpperCase() !== "[NO IMAGE]" &&
              !/^\[NO\s*IMAGE\]$/i.test(prev.imageUrl);
            const nextImageUrl = haveGoodAlready
              ? prev.imageUrl
              : isGoodImg
              ? normalizedImg
              : "";
            const nextName = title || prev.name;
            metaMapTitle.set(key, {
              key,
              name: nextName,
              imageUrl: nextImageUrl,
            });
          }
        }

        // 2) Load recent time-series, group by title
        // recent headers: universe_id, title, timestamp, ccu, peak_ccu, visits, daily_visits, cumulative_visits
        const recentText = await fetchGzipCsvText(ROBLOX_RECENT_URL);
        const recentRows = parseCSV(recentText);
        const recentHeaders =
          recentText
            .split(/\r?\n/)[0]
            ?.split(",")
            .map((h) => h.trim()) || [];

        const byTitle = new Map();
        for (const r of recentRows) {
          const title = (r.title || "").trim();
          const k = normTitle(title);
          if (!k) continue;

          const t = toDate(r.timestamp);
          if (!t) continue;

          const ccu = toNum(r.ccu);
          const visits = toNum(r.cumulative_visits);

          if (!byTitle.has(k)) byTitle.set(k, []);
          byTitle.get(k).push({ t, ccu, visits, title }); // keep last seen display title too
        }

        const ONE_DAY = 24 * 60 * 60 * 1000;
        const out = [];

        for (const [k, rows] of byTitle.entries()) {
          rows.sort((a, b) => a.t - b.t);
          const latest = rows[rows.length - 1];
          if (!latest) continue;

          const target = latest.t.getTime() - ONE_DAY;
          let closest = null;
          let bestDiff = Infinity;
          for (let i = rows.length - 2; i >= 0; i--) {
            const diff = Math.abs(rows[i].t.getTime() - target);
            if (diff < bestDiff) {
              bestDiff = diff;
              closest = rows[i];
            } else if (rows[i].t.getTime() < target && diff > bestDiff) {
              break;
            }
          }
          const ccuDoD = closest ? latest.ccu - closest.ccu : 0;

          const meta = metaMapTitle.get(k) || {
            key: k,
            name: latest.title || "(untitled)",
            imageUrl: "",
          };

          out.push({
            key: k, // normalized title
            name: meta.name || latest.title || "(untitled)",
            imageUrl: meta.imageUrl,
            ccu: toNum(latest.ccu),
            visits: toNum(latest.visits),
            ccuDoD,
          });
        }

        out.sort((a, b) => b.ccu - a.ccu);

        // ===== DEBUG =====
        const uniqueRecentTitles = byTitle.size;
        const joined = out.length;
        const joinedWithImage = out.filter(
          (x) => x.imageUrl && x.imageUrl !== "[NO IMAGE]"
        ).length;
        const sampleJoined = take(out, 25).map((x) => ({
          key: x.key,
          name: x.name,
          imageUrl: x.imageUrl,
        }));

        setDbg({
          dbHeaders,
          recentHeaders,
          dbRows: dbRows.length,
          recentRows: recentRows.length,
          metaMapSize: metaMapTitle.size,
          uniqueRecentTitles,
          joined,
          joinedWithImage,
          sampleJoined,
        });

        setGames(out);
      } catch (e) {
        console.error("Failed to load Roblox data:", e);
        if (!cancelled) setGames([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return games
      .filter(
        (g) => !hidden.has(g.key) && (g.name || "").toLowerCase().includes(q)
      )
      .map((g) => ({
        ...g,
        __onImgError: onImgError,
        __onImgLoad: onImgLoad,
        __imgFailed: imgErrors.has(g.key),
      }));
  }, [games, hidden, search, imgErrors]);

  const totalCCU = useMemo(
    () => filtered.reduce((s, g) => s + (g.ccu || 0), 0),
    [filtered]
  );

  return (
    <div
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 260,
          padding: 20,
          background: "#111",
          borderRight: "1px solid #333",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/roblox-sidebar.png`}
          alt="Roblox"
          style={{
            width: 180,
            height: "auto",
            display: "block",
            marginBottom: 10,
          }}
        />
        <input
          type="text"
          placeholder="Search games…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            background: "#111",
            border: "1px solid #444",
            borderRadius: 6,
            padding: "10px 12px",
            color: "#ccc",
            fontSize: 14,
          }}
        />
        <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>
          Click a card to see details.
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: "20px 30px 30px 30px" }}>
        <div
          style={{
            marginBottom: 12,
            color: "#aaa",
            fontSize: 13,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {loading
            ? "Loading…"
            : `${filtered.length.toLocaleString()} experiences · ${totalCCU.toLocaleString()} CCU total`}
          <button
            onClick={() => setHidden(new Set())}
            style={{
              marginLeft: "auto",
              background: "#222",
              border: "1px solid #555",
              color: "#fff",
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 12,
            }}
            title="Show all removed"
          >
            Reset Hidden
          </button>
        </div>

        <RobloxCardGrid
          games={filtered}
          loading={loading}
          onCardClick={(g) => setSelected(g)}
          onRemove={(key) => setHidden((prev) => new Set(prev).add(key))}
        />
      </div>

      {/* Popout */}
      {selected && (
        <GameModal game={selected} onClose={() => setSelected(null)} />
      )}

      {/* ===== DEBUG TOGGLER ===== */}
      <button
        onClick={() => setDbgOpen((v) => !v)}
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 99999,
          background: "#3fa9f5",
          color: "#000",
          border: "none",
          borderRadius: 999,
          padding: "10px 14px",
          fontWeight: 800,
          cursor: "pointer",
        }}
      >
        {dbgOpen ? "Hide Debug" : "Show Debug"}
      </button>

      {/* ===== DEBUG PANEL ===== */}
      {dbgOpen && (
        <div
          style={{
            position: "fixed",
            right: 0,
            top: 0,
            bottom: 0,
            width: 460,
            background: "#0b0b0b",
            borderLeft: "1px solid #333",
            zIndex: 99998,
            padding: 12,
            overflow: "auto",
            color: "#fff",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>
            Roblox Debug
          </div>

          <div style={{ fontSize: 13, color: "#cfd8e3" }}>
            <div>
              <b>DB headers:</b> {dbg.dbHeaders.join(" | ")}
            </div>
            <div>
              <b>Recent headers:</b> {dbg.recentHeaders.join(" | ")}
            </div>
            <div style={{ marginTop: 8 }}>
              <b>DB rows:</b> {dbg.dbRows.toLocaleString()} &nbsp;•&nbsp;
              <b>Recent rows:</b> {dbg.recentRows.toLocaleString()}
            </div>
            <div style={{ marginTop: 4 }}>
              <b>metaMap size (DB titles):</b>{" "}
              {dbg.metaMapSize.toLocaleString()}
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Recent unique titles:</b>{" "}
              {dbg.uniqueRecentTitles.toLocaleString()}
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Joined for cards:</b> {dbg.joined.toLocaleString()}{" "}
              &nbsp;•&nbsp;
              <b>With image (non-empty & not [NO IMAGE]):</b>{" "}
              {dbg.joinedWithImage.toLocaleString()}
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Image loads:</b> {imgLoads.size} &nbsp;•&nbsp;{" "}
              <b>Image errors:</b> {imgErrors.size}
            </div>
          </div>

          <hr style={{ borderColor: "#333", margin: "10px 0" }} />

          <div style={{ fontWeight: 800, marginBottom: 6 }}>
            Sample joined (click to open image)
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.4 }}>
            {dbg.sampleJoined.map((r) => (
              <div key={r.key} style={{ marginBottom: 6 }}>
                <div style={{ color: "#9cf" }}>{r.key}</div>
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {r.name}
                </div>
                <div>
                  {r.imageUrl ? (
                    <a
                      href={r.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#6cf" }}
                    >
                      {r.imageUrl}
                    </a>
                  ) : (
                    <span style={{ color: "#ccc" }}>(no image)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
